import { CreditCard } from 'lucide-react'
import { useState } from 'react'
import toast from 'react-hot-toast'

import { FeeSource, FeeType, type OrganizationFeeWithDefault } from '@ankor-io/common/types/pay/fee'
import { Badge, EmptyState, Heading, Loading, Switch, Text } from '@ankor-io/ui-components'

import { MIN_LOADING_DISPLAY_MS } from '@/constants/ui'
import { useMinimumDisplayTime } from '@/hooks/useMinimumDisplayTime'
import { useUser } from '@/hooks/useUser'
import {
  useBatchDeleteOrganizationFeesMutation,
  useOrganizationFeesQuery,
  useUpdateOrganizationFeesMutation,
} from '@/queries'

import { OrganizationFeesForm } from './organization-fees-form'

interface OrganizationFeesProps {
  organizationId: string
}
export function OrganizationFees({ organizationId }: OrganizationFeesProps) {
  const { user } = useUser()
  const { data: organizationFees, isPending, isError, error } = useOrganizationFeesQuery(organizationId)

  // Get user's full name for audit trail
  const getUserFullName = (): string => {
    if (!user) return 'Unknown'
    const { givenName, familyName } = user
    if (givenName && familyName) return `${givenName} ${familyName}`
    return givenName || familyName || 'Unknown'
  }
  const { mutate: updateFees, isPending: isUpdating } = useUpdateOrganizationFeesMutation(organizationId)
  const { mutateAsync: deleteFeesBatch, isPending: isDeleting } = useBatchDeleteOrganizationFeesMutation(organizationId)
  const feesArray = organizationFees?.fees || []

  // Find specific fees by source
  const conversionFee = feesArray.find((fee: OrganizationFeeWithDefault) => fee.feeSource === FeeSource.CONVERSION)
  const transferFee = feesArray.find((fee: OrganizationFeeWithDefault) => fee.feeSource === FeeSource.TRANSFER)

  // Determine if any fee has override enabled
  const hasAnyOverride = feesArray.some((fee: OrganizationFeeWithDefault) => fee.isOverride)

  // Local state for override toggle (starts from data, then user can toggle)
  const [isOverrideState, setIsOverrideState] = useState<boolean | null>(null)
  const effectiveIsOverride = isOverrideState ?? hasAnyOverride

  // Local state for fee amounts (controlled inputs) - using strings to allow empty input during editing
  const [conversionFeeAmount, setConversionFeeAmount] = useState<string | null>(null)
  const [transferFeeAmount, setTransferFeeAmount] = useState<string | null>(null)

  const effectiveConversionAmount =
    conversionFeeAmount !== null ? parseFloat(conversionFeeAmount) || 0 : (conversionFee?.feeAmount ?? 0)
  const effectiveTransferAmount =
    transferFeeAmount !== null ? parseFloat(transferFeeAmount) || 0 : (transferFee?.feeAmount ?? 0)

  // Check if any changes have been made
  const hasChanges =
    // Toggle changed
    (isOverrideState !== null && isOverrideState !== hasAnyOverride) ||
    // Conversion fee changed (only when override is enabled)
    (effectiveIsOverride && conversionFeeAmount !== null && effectiveConversionAmount !== conversionFee?.feeAmount) ||
    // Transfer fee changed (only when override is enabled)
    (effectiveIsOverride && transferFeeAmount !== null && effectiveTransferAmount !== transferFee?.feeAmount)

  const showLoader = useMinimumDisplayTime(isPending, MIN_LOADING_DISPLAY_MS)

  // Error toast is handled globally by QueryProvider
  // Component still handles error UI state (EmptyState below)

  if (showLoader) {
    return <Loading variant="spinner" />
  }
  if (isError) {
    return (
      <EmptyState
        heading="Failed to load fees"
        subheading={error?.message || 'An error occurred while loading organization fees.'}
        icon={<CreditCard className="mx-auto size-12 text-gray-400" />}
      />
    )
  }
  if (!organizationFees) {
    return (
      <EmptyState
        heading="No fees found"
        subheading="Get started by creating a new fee."
        icon={<CreditCard className="mx-auto size-12 text-gray-400" />}
      />
    )
  }

  const handleToggleChange = () => {
    const newOverrideState = !effectiveIsOverride
    setIsOverrideState(newOverrideState)

    // If switching to global defaults, reset values to global defaults
    if (!newOverrideState) {
      setConversionFeeAmount((conversionFee?.globalDefault?.feeAmount ?? 0).toString())
      setTransferFeeAmount((transferFee?.globalDefault?.feeAmount ?? 0).toString())
    } else {
      // If switching to override, initialize with current fee amounts
      setConversionFeeAmount((conversionFee?.feeAmount ?? 0).toString())
      setTransferFeeAmount((transferFee?.feeAmount ?? 0).toString())
    }
  }

  const handleSave = async () => {
    const useGlobalDefaults = !effectiveIsOverride

    if (useGlobalDefaults) {
      // If using global defaults, delete any existing fee overrides
      const feesToDelete = feesArray.filter((fee) => fee.isOverride)

      if (feesToDelete.length === 0) {
        console.error('No fee overrides to delete')
        return
      }

      // Use batch delete mutation - proper TanStack Query way
      // This mutation handles sequential deletion and single invalidation internally
      deleteFeesBatch(feesToDelete.map((fee) => fee.feeSource))
        .then(() => {
          // Reset local state after successful deletion
          setConversionFeeAmount(null)
          setTransferFeeAmount(null)
          setIsOverrideState(null)
          toast.success('Fees updated successfully')
        })
        .catch((error: unknown) => {
          toast.error(`Failed to delete fee overrides: ${error instanceof Error ? error.message : 'Unknown error'}`)
        })
    } else {
      // If using custom overrides, update the fees
      updateFees(
        {
          fees: [
            {
              feeSource: FeeSource.CONVERSION,
              feeType: FeeType.PERCENTAGE,
              feeAmount: effectiveConversionAmount,
              updatedByName: getUserFullName(),
              updatedReason: 'Updated via Lighthouse admin interface',
            },
            {
              feeSource: FeeSource.TRANSFER,
              feeType: FeeType.PERCENTAGE,
              feeAmount: effectiveTransferAmount,
              updatedByName: getUserFullName(),
              updatedReason: 'Updated via Lighthouse admin interface',
            },
          ],
        },
        {
          onSuccess: () => {
            // Reset local state after successful save
            setConversionFeeAmount(null)
            setTransferFeeAmount(null)
            setIsOverrideState(null)
            toast.success('Fees updated successfully')
          },
          onError: (error) => {
            toast.error(`Failed to update fees: ${error.message || 'Unknown error'}`)
          },
        },
      )
    }
  }

  const useGlobalDefaults = !effectiveIsOverride

  return (
    <div className="space-y-4">
      <div className="flex flex-col md:gap-1 p-2 rounded-md border border-gray-200 dark:border-gray-700">
        <div className="flex flex-col md:flex-row items-center justify-between p-4">
          <div className="flex flex-col gap-1">
            <Heading level={4}>Pricing Configuration</Heading>
            <Text className="text-sm text-gray-600 dark:text-gray-400">Manage the fees for your organization</Text>
          </div>
          <div className="flex items-center gap-4 py-5">
            <Badge color={effectiveIsOverride ? 'gray' : 'green'}>Use Global Defaults</Badge>

            <Switch
              checked={!effectiveIsOverride}
              disabled={false}
              onChange={handleToggleChange}
            />
          </div>
        </div>
        <div className="space-y-6 px-4 pb-4">
          <OrganizationFeesForm
            useGlobalDefaults={useGlobalDefaults}
            conversionFeeValue={
              useGlobalDefaults
                ? (conversionFee?.globalDefault?.feeAmount ?? 0).toString()
                : (conversionFeeAmount ?? (conversionFee?.feeAmount ?? 0).toString())
            }
            transferFeeValue={
              useGlobalDefaults
                ? (transferFee?.globalDefault?.feeAmount ?? 0).toString()
                : (transferFeeAmount ?? (transferFee?.feeAmount ?? 0).toString())
            }
            onConversionFeeChange={setConversionFeeAmount}
            onTransferFeeChange={setTransferFeeAmount}
            onSubmit={handleSave}
            hasChanges={hasChanges}
            isSubmitting={isUpdating || isDeleting}
          />
        </div>
      </div>
    </div>
  )
}

export default OrganizationFees
